import { baseArkhaleAPI } from './base-api'
import { Pagination } from '../../shared/models/others/pagination'
import {
  Alarm,
  AlarmComment,
  AlarmQuery,
  PostAlarmComment,
} from '../../shared/models/alarm.model'
import UserId from '../../shared/models/id/user-id'
import { AlarmID } from '../../shared/models/id/alarm-id'
import { formatSortOrder, SortOrder, UserQuery } from './query-type'
import User from '../../shared/models/user'

const alarmApi = baseArkhaleAPI.injectEndpoints({
  endpoints: (build) => ({
    getAlarms: build.query<Pagination<Alarm>, AlarmQuery>({
      query: (args) => ({
        url: 'alarms',
        method: 'GET',
        params: {
          offset: args.offset,
          limit: args.limit,
          textSearch: args.textSearch,
          ...formatSortOrder(args.sortOrder),
        },
      }),
    }),
    getAlarm: build.query<Alarm, AlarmID>({
      query: (id) => ({
        url: `alarms/${id.id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Alarm', id: id.id }],
    }),
    getAlarmCount: build.query<number, AlarmQuery>({
      query: (args) => ({
        url: 'alarms/count',
        method: 'GET',
        params: {
          textSearch: args.textSearch,
        },
      }),
    }),
    getAssignableUsers: build.query<
      Pagination<User>,
      UserQuery & { id: AlarmID }
    >({
      query: (args) => ({
        url: `alarms/${args.id.id}/assignable-users`,
        method: 'GET',
        params: {
          textSearch: args.textSearch,
        },
      }),
    }),
    assignAlarm: build.mutation<Alarm, { id: AlarmID; assigneeID?: UserId }>({
      query: ({ id, assigneeID }) => ({
        url: `alarms/${id.id}/assign`,
        method: 'PATCH',
        body: {
          assigneeID: assigneeID,
        },
      }),
    }),
    clearAlarm: build.mutation<Alarm, AlarmID>({
      query: (id) => ({
        url: `alarms/${id.id}/clear`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Alarm', id: id.id }],
    }),
    acknowledgeAlarm: build.mutation<Alarm, AlarmID>({
      query: (id) => ({
        url: `alarms/${id.id}/acknowledge`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Alarm', id: id.id }],
    }),
    getAlarmComments: build.query<
      AlarmComment[],
      { alarmID: AlarmID; sortOrder: SortOrder }
    >({
      query: ({ alarmID, sortOrder }) => ({
        url: `alarms/${alarmID.id}/comments`,
        method: 'GET',
        params: {
          sortOrder: sortOrder.direction,
          sortProperty: 'createdAt',
        },
      }),
      providesTags: (result, error, args) => [
        { type: 'Alarm', id: args.alarmID.id },
        { type: 'AlarmComment', id: args.alarmID.id },
      ],
    }),
    addComment: build.mutation<AlarmComment, PostAlarmComment>({
      query: ({ alarmID, text }) => ({
        url: `alarms/${alarmID.id}/comments`,
        method: 'POST',
        body: {
          text: text,
        },
      }),
      invalidatesTags: (result, error, { alarmID }) => [
        { type: 'AlarmComment', id: alarmID.id },
      ],
    }),
  }),
})

export const {
  useLazyGetAlarmsQuery,
  useGetAlarmQuery,
  useLazyGetAlarmQuery,
  useLazyGetAlarmCountQuery,
  useGetAssignableUsersQuery,
  useLazyGetAssignableUsersQuery,
  useAssignAlarmMutation,
  useClearAlarmMutation,
  useAcknowledgeAlarmMutation,
  useGetAlarmCommentsQuery,
  useAddCommentMutation,
} = alarmApi
